import React, { useState } from "react";
import PropTypes from "prop-types";
import { Button, Card, CardHeader, Select, TextInput } from "@opendate-app/setlist";
import client from "../../../shared/api/client";
import { updateTeamSetting } from "../../../shared/api/team";

const DEFAULTS = [
  {
    column: "default_general_ledger_code_id",
    label: "Account Code",
    hint: "Face-value ticket revenue.",
  },
  {
    column: "default_organizer_fee_general_ledger_code_id",
    label: "Organizer Fee GL Code",
    hint: "Price band organizer fees.",
  },
];

const NEW_CODE = "__new__";
const BLANK_CODE = { code: "", name: "" };

const codeValue = (id) => (id ? String(id) : "");

const GeneralLedgerDefaultSettings = ({ teamId, generalLedgerCodes, initialCodeIds }) => {
  const [codes, setCodes] = useState(generalLedgerCodes);
  const [codeIds, setCodeIds] = useState(() => (
    DEFAULTS.reduce((ids, { column }) => ({ ...ids, [column]: codeValue(initialCodeIds[column]) }), {})
  ));
  const [savingColumns, setSavingColumns] = useState({});
  const [newCodeColumn, setNewCodeColumn] = useState(null);
  const [newCode, setNewCode] = useState(BLANK_CODE);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const options = [
    { value: "", label: "No default" },
    ...codes.map((code) => ({ value: String(code.id), label: `${code.code} — ${code.name}` })),
    { value: NEW_CODE, label: "+ Add a new GL code…" },
  ];

  const change = (column, value) => {
    if (value === NEW_CODE) {
      setNewCodeColumn(column);
      setNewCode(BLANK_CODE);
      setError(null);
      return;
    }

    const previous = codeIds[column];
    setCodeIds((ids) => ({ ...ids, [column]: value }));
    setSavingColumns((columns) => ({ ...columns, [column]: true }));
    return updateTeamSetting(teamId, { [column]: value === "" ? null : value }).then((saved) => {
      if (!saved) setCodeIds((ids) => ({ ...ids, [column]: previous }));
      setSavingColumns((columns) => ({ ...columns, [column]: false }));
    });
  };

  const createCode = async () => {
    setSaving(true);
    setError(null);
    try {
      const { data: created } = await client.post(`/teams/${teamId}/general_ledger_codes.json`, {
        general_ledger_code: { code: newCode.code.trim(), name: newCode.name.trim() },
      });
      setCodes((current) => (
        current.some((code) => code.id === created.id)
          ? current
          : [...current, created].sort((a, b) => String(a.code).localeCompare(String(b.code)))
      ));
      setNewCodeColumn(null);
      await change(newCodeColumn, String(created.id));
    } catch (e) {
      const messages = e && e.response && e.response.data;
      setError(Array.isArray(messages) ? messages.join(", ") : "Couldn't add that GL code. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const canCreate = newCode.code.trim() !== "" && newCode.name.trim() !== "" && !saving;

  return (
    <div className="mt-4" data-testid="general-ledger-default-settings">
      <Card
        header={(
          <CardHeader
            title="General ledger"
            supportingText="New events start with these codes. You can still change them on any event's ticketing settings."
          />
        )}
      >
        <div className="d-flex flex-column" style={{ gap: 20, maxWidth: 420 }}>
          {DEFAULTS.map(({ column, label, hint }) => (
            <div key={column}>
              <Select
                id={`general-ledger-${column}`}
                label={label}
                hint={hint}
                options={options}
                value={codeIds[column]}
                disabled={Boolean(savingColumns[column])}
                onChange={(value) => change(column, value)}
              />
              {newCodeColumn === column ? (
                <div
                  className="p-3 mt-2"
                  data-testid="new-general-ledger-code"
                  style={{ border: "1px solid var(--sl-border-secondary)", borderRadius: "8px" }}
                >
                  <p className="small mb-2">
                    <strong>New GL code</strong> — this is added to your chart of accounts and becomes available
                    everywhere you pick a GL code.
                  </p>
                  <div className="d-flex" style={{ gap: "12px" }}>
                    <div style={{ flex: 1 }}>
                      <TextInput
                        size="sm"
                        id="new-general-ledger-code-code"
                        label="Code"
                        placeholder="41000"
                        value={newCode.code}
                        disabled={saving}
                        onChange={(e) => setNewCode({ ...newCode, code: e.target.value })}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <TextInput
                        size="sm"
                        id="new-general-ledger-code-name"
                        label="Name"
                        placeholder="Ticket Revenue"
                        value={newCode.name}
                        disabled={saving}
                        onChange={(e) => setNewCode({ ...newCode, name: e.target.value })}
                      />
                    </div>
                  </div>
                  {error ? <p className="small text-danger mt-2 mb-0">{error}</p> : null}
                  <div className="d-flex mt-2" style={{ gap: "8px" }}>
                    <Button variant="primary" size="sm" disabled={!canCreate} onClick={createCode} data-testid="new-general-ledger-code-save">
                      Add GL code
                    </Button>
                    <Button variant="secondary" size="sm" disabled={saving} onClick={() => setNewCodeColumn(null)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

GeneralLedgerDefaultSettings.propTypes = {
  teamId: PropTypes.number.isRequired,
  generalLedgerCodes: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.number.isRequired, code: PropTypes.string, name: PropTypes.string })
  ).isRequired,
  initialCodeIds: PropTypes.objectOf(PropTypes.number).isRequired,
};

export default GeneralLedgerDefaultSettings;
